import React, { forwardRef, useState } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollar, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useAuthContext } from 'contexts/AuthContext';
import { IOptionAnyValue } from 'bricks/types';
import useToast from 'hooks/useToast';
import NSSwitch from 'bricks/NSSwitch/NSSwitch';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { Deal } from 'views/Deals/types';
import { UnitTypeIds } from 'constants/unitTypes';
import RenderIf from 'components/RenderIf/RenderIf';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { UNIT_TYPES } from 'views/Deals/constants';
import { URLS } from 'services/urls';
import { DealTypeToId } from 'constants/deals';
import { useCreateDeal } from './hooks/useCreateDeal';
import { DefaultAttributes, IFormValues } from './types';
import {
    buildDealSchemaFields,
} from './utils';
import { useUpdateDeal } from './hooks/useUpdateDeal';
import { useGetAttributes } from './hooks/useGetAttributes';
import DefaultAttributeInput from './components/DefaultAttributeInput';
import DefaultAttributeDropdown from './components/DefaultAttributeDropdown';
import AddressSection from './components/AddressSection';
import UnitsSection from './components/UnitsSection';
import { useGetDealFormSelectOptions } from './hooks/useGetDealFormSelectOptions';
import CustomAttributeInput from './components/CustomAttributeInput/CustomAttributeInput';
import { useGetOrganizationCompanies } from './hooks/useGetOrganizationCompanies';
import NoOrganizationCompanies from './components/NoOrganizationCompanies/NoOrganizationCompanies';
import DevelopmentManagerInput from './components/DevelopmentManagerInput/DevelopmentManagerInput';
import { AssetTypeIds, ConstructionTypeIds, FIRST_DEAL_DEFAULTS } from './constants';
import { useGetDeals } from '../../views/Deals/hooks/useFetchDeals';

interface DealFormProps {
    setMapCoordinates: React.Dispatch<React.SetStateAction<{ center: { lat: number; lng: number } }>>;
    initialValues?: Deal;
    onSubmitting?: (isSubmitting: boolean) => void;
}

const DealForm = forwardRef<FormikProps<IFormValues>, DealFormProps>(({ setMapCoordinates, initialValues, onSubmitting }, ref) => {
    const navigate = useNavigate();
    const { selectedOrganizationId } = useAuthContext();
    const { showSuccess, showError } = useToast();
    const {
        data: {
            assetTypeOptions,
            dealTypeOptions,
            leadSourceOptions,
            leasingStrategyOptions,
            rankOptions,
            constructionTypeOptions,
            phaseOptions,
            marketOptions,
            organizationUserOptions,
            organizationContactOptions,
        },
        getSelectedAssetType,
        loadingStates,
    } = useGetDealFormSelectOptions(selectedOrganizationId);
    const { data: attributesConfig, isLoading: isLoadingAttributes } = useGetAttributes();
    const customAttributes = attributesConfig?.filter(({ isCustom }) => isCustom) || [];
    const { data: organizationCompanies, isLoading: isLoadingOrganizationCompanies } = useGetOrganizationCompanies({
        refetchInterval: query => {
            const companies = query.state.data;
            if (!companies || companies.length === 0) {
                return 5000; // Keep polling if no companies exist
            }
            return false; // Stop polling once we get companies
        },
    });

    const organizationCompanyOptions = organizationCompanies?.map(({ correlationId, name }) => ({
        label: name,
        value: correlationId,
    })) || [];
    const hasCompanyOptions = !!organizationCompanyOptions.length;

    const getSubTypeOptions = (assetTypeId: string): IOptionAnyValue<string>[] => getSelectedAssetType(assetTypeId)?.subAssetTypes
        ?.filter(({ description }) => description)
        ?.map(({ correlationId, description }) => ({
            label: description!,
            value: correlationId,
        })) || [];

    const initialSubTypeOptions = initialValues?.assetType?.correlationId
        ? getSubTypeOptions(initialValues.assetType.correlationId)
        : getSubTypeOptions(AssetTypeIds.RentalApartment);

    const [subTypeOptions, setSubTypeOptions] = useState<IOptionAnyValue<string>[]>(initialSubTypeOptions);

    const [showSuggestedOnly, setShowSuggestedOnly] = useLocalStorage<boolean>(
        PageSpecificKeys.NewDealFormShowSuggestedOnly,
        true,
    );
    const toggleShowSuggestedOnly = () => {
        setShowSuggestedOnly(!showSuggestedOnly);
    };

    const isCreateMode = !initialValues;
    const isCollapsedCreateView = isCreateMode && showSuggestedOnly;

    const { mutate: createDeal } = useCreateDeal();
    const { mutate: updateDeal } = useUpdateDeal();

    // Check if user has any existing deals (to pre-populate fake address for first deal)
    const { data: dealsData } = useGetDeals({
        pageSize: 1,
        currentPage: 1,
        orgId: selectedOrganizationId!,
    });
    const isFirstDeal = !initialValues && dealsData?.itemCount === 0;

    const location = useLocation();
    const scenarioId = location.state?.scenarioId;

    const handleSubmit = async (values: IFormValues) => {
        const updatedValues = { ...values };

        Object.values(UNIT_TYPES).forEach(key => {
            const field = key as keyof IFormValues;
            if (updatedValues[field] === '') {
                updatedValues[field] = undefined;
            }
        });

        if (updatedValues.coordinatesSwitch === false) {
            // @ts-ignore
            updatedValues.latitude = null;
            // @ts-ignore
            updatedValues.longitude = null;
        } else {
            updatedValues.address = null;
            updatedValues.city = null;
            updatedValues.state = null;
            updatedValues.zipCode = null;
            updatedValues.county = null;
        }

        if (updatedValues.developmentManager && !updatedValues.developmentManager.correlationId) {
            updatedValues.developmentManager = undefined;
        }
        // @ts-ignore
        if (updatedValues.acquisitionPrice === '') {
            updatedValues.acquisitionPrice = null;
        }

        if (isLoadingAttributes) return;

        onSubmitting?.(true);

        if (initialValues && initialValues?.id) {
            updateDeal(
                {
                    orgId: selectedOrganizationId!,
                    dealId: Number(initialValues.id),
                    postData: updatedValues,
                },
                {
                    onSuccess: data => {
                        navigate(-1);
                        setTimeout(() => showSuccess('Deal updated successfully!'), 500);
                    },
                    onError: error => {
                        onSubmitting?.(false);
                        console.error('Error updating deal:', error);
                        showError('Failed to update deal. Please try again.');
                    },
                },
            );
        } else {
            createDeal(
                {
                    orgId: selectedOrganizationId!,
                    postData: {
                        ...updatedValues,
                        ...(scenarioId && typeof scenarioId === 'number' ? { scenarioId } : {}),
                    },
                },
                {
                    onSuccess: data => {
                        showSuccess('Deal created successfully!');
                        const dealsPath = generatePath(URLS.DEAL_DASHBOARD.HOME, { id: data.id, dealId: data.id, tab: 'overview' });
                        navigate(dealsPath);
                    },
                    onError: error => {
                        onSubmitting?.(false);
                        console.error('Error creating deal:', error);
                        showError('Failed to create deal. Please try again.');
                    },
                },
            );
        }
    };
    const opportunityPhaseId = phaseOptions?.find(phase => phase.label === 'Opportunity')?.value;

    const defaultValues: IFormValues = {
        dealName: initialValues?.dealName || (isFirstDeal ? FIRST_DEAL_DEFAULTS.name : 'Deal'),
        address: initialValues?.address || (isFirstDeal ? FIRST_DEAL_DEFAULTS.address : undefined),
        city: initialValues?.city || (isFirstDeal ? FIRST_DEAL_DEFAULTS.city : undefined),
        county: initialValues?.county || (isFirstDeal ? FIRST_DEAL_DEFAULTS.county : undefined),
        state: initialValues?.state || (isFirstDeal ? FIRST_DEAL_DEFAULTS.state : undefined),
        latitude: initialValues?.latitude || undefined,
        longitude: initialValues?.longitude || undefined,
        zipCode: initialValues?.zipCode || (isFirstDeal ? FIRST_DEAL_DEFAULTS.zipCode : undefined),
        coordinatesSwitch: Boolean(initialValues && !initialValues.address && initialValues?.latitude && initialValues?.longitude),
        description: initialValues?.description || undefined,
        acquisitionPrice: initialValues?.acquisitionPrice,
        marketId: initialValues?.market?.correlationId,
        submarket: initialValues?.submarket || undefined,
        dealTypeId: initialValues?.dealType?.id,
        assetTypeId: initialValues?.assetType?.correlationId,
        assetSubtypeId: initialValues?.assetSubtype?.correlationId,
        constructionTypeId: initialValues?.constructionType?.correlationId,
        phaseId: initialValues?.subprojectStage?.correlationId || opportunityPhaseId,
        dealOwnerId: initialValues?.dealOwner?.correlationId,
        leadSourceId: initialValues?.leadSource?.id,
        leasingStrategyId: initialValues?.leasingStrategy?.id,
        rankId: initialValues?.rank?.id,
        grossBuildSf: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.GROSS_SF)?.value,
        rentableSf: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.RENTABLE_SF)?.value,
        finishedSf: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.FINISHED_SF)?.value,
        acres: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.ACRE)?.value,
        residentialUnits: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.RESIDENTIAL_UNIT)?.value,
        lots: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.LOT)?.value,
        megawatts: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.MEGAWATT)?.value,
        beds: initialValues?.dealUnitTypes?.find(unitType => unitType.unitType === UnitTypeIds.BED)?.value,
        investorLpId: initialValues?.investorLp?.id,
        lenderPrivateId: initialValues?.lenderPrivate?.id,
        legalServicesId: initialValues?.legalServices?.id,
        projectArchitectId: initialValues?.projectArchitect?.id,
        surveyorId: initialValues?.surveyor?.id,
        environmentalConsultantId: initialValues?.environmentalConsultant?.id,
        engineerId: initialValues?.engineer?.id,
        customAttributes: initialValues?.customAttributes?.map(
            ({
                attributeId, valueText, valueNumber, valueSingleSelect, valueMultiSelect,
            }) => ({
                attributeId,
                ...(valueText && { valueText }),
                ...(valueNumber && { valueNumber }),
                ...(valueSingleSelect && { valueSingleSelect }),
                ...(valueMultiSelect && { valueMultiSelect }),
            }),
        ) || [],
        developmentManager: initialValues?.developmentManager || undefined,
    };

    const hasRequiredFieldInSection = (attributes: DefaultAttributes[]) => {
        if (!showSuggestedOnly) return true;
        return attributes.some(attr => attributesConfig?.find(config => config.name === attr)?.required);
    };

    const hasRequiredCustomAttributes = () => {
        if (!showSuggestedOnly) return true;
        return customAttributes.some(attr => attr.required);
    };

    return (
        <div className="w-50 p-3 mr-2 CreateDeal__form">
            <div className="d-flex justify-content-between text-white mb-1">
                <div className="text-uppercase text-primary">
                    General
                    {isLoadingAttributes && (
                        <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            className=""
                            size="sm"
                        />
                    )}
                </div>
                <NSSwitch
                    id="showSuggestedOnly"
                    name="showSuggestedOnly"
                    checked={showSuggestedOnly}
                    onChange={toggleShowSuggestedOnly}
                    label="Only show suggested fields"
                />
            </div>
            <Formik<IFormValues>
                initialValues={defaultValues}
                validationSchema={buildDealSchemaFields(attributesConfig)}
                onSubmit={handleSubmit}
                innerRef={ref}
                enableReinitialize
            >
                <Form>
                    <DefaultAttributeInput
                        label="Deal name"
                        name="dealName"
                        placeholder="Enter deal name"
                        attribute={DefaultAttributes.NAME}
                        showSuggestedOnly={showSuggestedOnly}
                    />
                    <RenderIf isTrue={!isCollapsedCreateView}>
                        <AddressSection
                            showSuggestedOnly={showSuggestedOnly}
                            setMapCoordinates={setMapCoordinates}
                        />
                    </RenderIf>
                    <DefaultAttributeDropdown
                        label="Market"
                        name="marketId"
                        options={marketOptions}
                        attribute={DefaultAttributes.MARKET}
                        showSuggestedOnly={showSuggestedOnly}
                        className="mt-2"
                        isLoading={loadingStates.markets}
                        customPlaceholder="Select market"
                        shouldUseSelect
                    />
                    <DefaultAttributeInput
                        label="Submarket"
                        name="submarket"
                        placeholder="Enter submarket"
                        attribute={DefaultAttributes.SUBMARKET}
                        showSuggestedOnly={showSuggestedOnly}
                        className="mt-2 w-50"
                    />
                    <DefaultAttributeInput
                        label="Description"
                        name="description"
                        placeholder="Enter deal description"
                        attribute={DefaultAttributes.DESCRIPTION}
                        showSuggestedOnly={showSuggestedOnly}
                        type="textarea"
                        rows={4}
                        className="mt-2"
                    />

                    <div className="CreateDeal__form__divider my-3" />
                    <div>
                        <div className="text-uppercase text-primary mb-1">
                            Deal Information
                            {isLoadingAttributes && (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    className=""
                                    size="sm"
                                />
                            )}
                        </div>
                        <div className="d-flex mt-2">
                            <DefaultAttributeDropdown
                                label="Deal type"
                                name="dealTypeId"
                                options={dealTypeOptions}
                                defaultValue={DealTypeToId.Development}
                                attribute={DefaultAttributes.DEAL_TYPE}
                                showSuggestedOnly={showSuggestedOnly}
                                additionalRenderCondition={!isCollapsedCreateView}
                                className="w-50 mr-2"
                                isLoading={loadingStates.dealTypes}
                            />
                            <DefaultAttributeDropdown
                                label="Asset class"
                                name="assetTypeId"
                                options={assetTypeOptions}
                                defaultValue={AssetTypeIds.RentalApartment}
                                attribute={DefaultAttributes.ASSET_CLASS}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50"
                                isLoading={loadingStates.assetTypes}
                                onSelect={(option, setFieldValue) => {
                                    const newSubTypeOptions = option?.value ? getSubTypeOptions(option.value) : [];
                                    setSubTypeOptions(newSubTypeOptions);
                                    setFieldValue('hasSubtype', newSubTypeOptions.length > 0);
                                    setFieldValue('assetSubtypeId', newSubTypeOptions.length > 0 ? newSubTypeOptions[0].value : undefined);
                                }}
                            />
                            <DefaultAttributeDropdown
                                label="Subtype"
                                name="assetSubtypeId"
                                options={subTypeOptions}
                                defaultValue={subTypeOptions.length > 0 ? subTypeOptions[0].value : undefined}
                                attribute={DefaultAttributes.ASSET_CLASS}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50 ml-2"
                                additionalRenderCondition={subTypeOptions.length > 0}
                                additionalRequiredCondition={!!subTypeOptions.length}
                            />
                        </div>
                        <UnitsSection showSuggestedOnly={showSuggestedOnly} />
                        <div className="d-flex mt-2">
                            <DefaultAttributeDropdown
                                label="Construction type"
                                name="constructionTypeId"
                                options={constructionTypeOptions}
                                defaultValue={ConstructionTypeIds.GroundUp}
                                attribute={DefaultAttributes.CONSTRUCTION_TYPE}
                                showSuggestedOnly={showSuggestedOnly}
                                additionalRenderCondition={!isCollapsedCreateView}
                                className="w-50 mr-2"
                                isLoading={loadingStates.constructionTypes}
                            />
                            <DefaultAttributeDropdown
                                label="Phase"
                                name="phaseId"
                                options={phaseOptions}
                                attribute={DefaultAttributes.PHASE}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50"
                                isLoading={loadingStates.phases}
                            />
                        </div>

                        <div className="d-flex mt-2">
                            <DefaultAttributeDropdown
                                label="Lead source"
                                name="leadSourceId"
                                options={leadSourceOptions}
                                attribute={DefaultAttributes.LEAD_SOURCE}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50"
                                isLoading={loadingStates.leadSources}
                            />
                            <DefaultAttributeDropdown
                                label="Leasing strategy"
                                name="leasingStrategyId"
                                options={leasingStrategyOptions}
                                attribute={DefaultAttributes.LEASING_STRATEGY}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50 ml-2"
                                isLoading={loadingStates.leasingStrategies}
                            />
                            <DefaultAttributeDropdown
                                label="Rank"
                                name="rankId"
                                options={rankOptions}
                                attribute={DefaultAttributes.RANK}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50 ml-2"
                                isLoading={loadingStates.ranks}
                            />
                        </div>
                    </div>

                    <RenderIf isTrue={hasRequiredFieldInSection([DefaultAttributes.ACQUISITION_PRICE])}>
                        <div className="CreateDeal__form__divider my-3" />
                        <div className="text-uppercase text-primary mb-1">
                            Deal Metrics
                            {isLoadingAttributes && (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    className=""
                                    size="sm"
                                />
                            )}
                        </div>
                        <DefaultAttributeInput
                            label="Acquisition price"
                            name="acquisitionPrice"
                            placeholder="Enter amount"
                            type="number"
                            attribute={DefaultAttributes.ACQUISITION_PRICE}
                            showSuggestedOnly={showSuggestedOnly}
                            prependInputAddon={<FontAwesomeIcon color="white" icon={faDollar} />}
                            className="mb-3"
                        />
                    </RenderIf>

                    <RenderIf isTrue={hasRequiredFieldInSection([DefaultAttributes.DEAL_OWNER, DefaultAttributes.DEVELOPMENT_MANAGER])}>
                        <div className="CreateDeal__form__divider my-3" />
                        <div className="mb-3">
                            <div className="text-uppercase text-primary mb-1">
                                Personnel
                                {isLoadingAttributes && (
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        spin
                                        className=""
                                        size="sm"
                                    />
                                )}
                            </div>
                            <div className="d-flex mt-2">
                                <DefaultAttributeDropdown
                                    label="Deal owner"
                                    name="dealOwnerId"
                                    options={organizationUserOptions}
                                    attribute={DefaultAttributes.DEAL_OWNER}
                                    showSuggestedOnly={showSuggestedOnly}
                                    className="w-50 mr-2"
                                    isLoading={loadingStates.organizationUsers}
                                    shouldUseSelect
                                />
                                <DevelopmentManagerInput
                                    showSuggestedOnly={showSuggestedOnly}
                                    contactOptions={organizationContactOptions}
                                    contactsAreLoading={loadingStates.organizationContacts}
                                    companyOptions={organizationCompanyOptions}
                                    companiesAreLoading={isLoadingOrganizationCompanies}
                                    userOptions={organizationUserOptions}
                                    usersAreLoading={loadingStates.organizationUsers}
                                />
                            </div>
                        </div>
                    </RenderIf>
                    <RenderIf isTrue={hasRequiredFieldInSection([DefaultAttributes.INVESTOR_LP, DefaultAttributes.LENDER_PRIVATE])}>
                        <div className="CreateDeal__form__divider my-3" />
                        <div className="text-uppercase text-primary mb-1">
                            Capital Partners
                            {isLoadingAttributes && (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    className=""
                                    size="sm"
                                />
                            )}
                        </div>
                        <div className="d-flex mt-2">
                            <DefaultAttributeDropdown
                                label="Investor - LP"
                                name="investorLpId"
                                options={organizationCompanyOptions}
                                attribute={DefaultAttributes.INVESTOR_LP}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50 mr-2"
                                isLoading={isLoadingOrganizationCompanies}
                                isDisabled={!hasCompanyOptions}
                                shouldUseSelect
                                isClearable
                            />
                            <DefaultAttributeDropdown
                                label="Lender (private)"
                                name="lenderPrivateId"
                                options={organizationCompanyOptions}
                                attribute={DefaultAttributes.LENDER_PRIVATE}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50"
                                isLoading={isLoadingOrganizationCompanies}
                                isDisabled={!hasCompanyOptions}
                                shouldUseSelect
                                isClearable
                            />
                        </div>
                        <NoOrganizationCompanies shouldRender={!hasCompanyOptions} companyType="capital partners" />
                    </RenderIf>
                    <RenderIf isTrue={hasRequiredFieldInSection([DefaultAttributes.LEGAL_SERVICES, DefaultAttributes.PROJECT_ARCHITECT,
                        DefaultAttributes.SURVEYOR, DefaultAttributes.ENVIRONMENTAL_CONSULTANT, DefaultAttributes.ENGINEER])}
                    >
                        <div className="CreateDeal__form__divider my-3" />
                        <div className="text-uppercase text-primary mb-1">
                            Vendors
                            {isLoadingAttributes && (
                                <FontAwesomeIcon
                                    icon={faSpinner}
                                    spin
                                    className=""
                                    size="sm"
                                />
                            )}
                        </div>
                        <div className="d-flex mt-2">
                            <DefaultAttributeDropdown
                                label="Legal services"
                                name="legalServicesId"
                                options={organizationCompanyOptions}
                                attribute={DefaultAttributes.LEGAL_SERVICES}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50 mr-2"
                                isLoading={isLoadingOrganizationCompanies}
                                isDisabled={!hasCompanyOptions}
                                shouldUseSelect
                                isClearable
                            />
                            <DefaultAttributeDropdown
                                label="Project architect"
                                name="projectArchitectId"
                                options={organizationCompanyOptions}
                                attribute={DefaultAttributes.PROJECT_ARCHITECT}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50"
                                isLoading={isLoadingOrganizationCompanies}
                                isDisabled={!hasCompanyOptions}
                                shouldUseSelect
                                isClearable
                            />
                        </div>
                        <div className="d-flex mt-2">
                            <DefaultAttributeDropdown
                                label="Surveyor"
                                name="surveyorId"
                                options={organizationCompanyOptions}
                                attribute={DefaultAttributes.SURVEYOR}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50 mr-2"
                                isLoading={isLoadingOrganizationCompanies}
                                isDisabled={!hasCompanyOptions}
                                shouldUseSelect
                                isClearable
                            />
                            <DefaultAttributeDropdown
                                label="Environmental consultant"
                                name="environmentalConsultantId"
                                options={organizationCompanyOptions}
                                attribute={DefaultAttributes.ENVIRONMENTAL_CONSULTANT}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50"
                                isLoading={isLoadingOrganizationCompanies}
                                isDisabled={!hasCompanyOptions}
                                shouldUseSelect
                                isClearable
                            />
                        </div>
                        <div className="d-flex mt-2">
                            <DefaultAttributeDropdown
                                label="Engineer"
                                name="engineerId"
                                options={organizationCompanyOptions}
                                attribute={DefaultAttributes.ENGINEER}
                                showSuggestedOnly={showSuggestedOnly}
                                className="w-50 pr-1"
                                isLoading={isLoadingOrganizationCompanies}
                                isDisabled={!hasCompanyOptions}
                                shouldUseSelect
                                isClearable
                            />
                        </div>
                        <NoOrganizationCompanies shouldRender={!hasCompanyOptions} companyType="vendors" />
                    </RenderIf>

                    <RenderIf isTrue={customAttributes.length > 0 && hasRequiredCustomAttributes()}>
                        <div className="CreateDeal__form__divider my-3" />
                        <div className="mb-3">
                            <div className="text-uppercase text-primary mb-1">
                                Custom Attributes
                                {isLoadingAttributes && (
                                    <FontAwesomeIcon
                                        icon={faSpinner}
                                        spin
                                        className="ml-2"
                                        size="sm"
                                    />
                                )}
                            </div>
                            <div className="d-flex flex-wrap">
                                {customAttributes.map((attribute, index) => (
                                    <CustomAttributeInput
                                        key={attribute.correlationId}
                                        attribute={attribute}
                                        className="w-50 pr-2 mt-2"
                                        showSuggestedOnly={showSuggestedOnly}
                                        isLoading={isLoadingAttributes}
                                    />
                                ))}
                            </div>
                        </div>
                    </RenderIf>
                </Form>
            </Formik>
        </div>
    );
});

export default DealForm;
